/**
 * Converts a DOM selection inside rendered chat markdown back into markdown
 * source so highlight-and-copy keeps formatting (links, emphasis, lists,
 * fences, tables) instead of flattening to plain text. The `text/plain`
 * clipboard flavor carries the markdown; `text/html` carries a sanitized
 * copy of the rendered fragment for rich-paste targets.
 */

const SKIPPED_TAGS = new Set(["BUTTON", "INPUT", "SCRIPT", "STYLE", "TEMPLATE"]);
const SKIPPED_CLASS_NAMES = ["select-none", "sr-only"];

function isSkippedElement(element: Element): boolean {
  if (SKIPPED_TAGS.has(element.tagName) || element.localName === "svg") return true;
  if (element.getAttribute("aria-hidden") === "true") return true;
  return SKIPPED_CLASS_NAMES.some((className) => element.classList.contains(className));
}

/** Hoists surrounding whitespace outside the markers: "` bold `" → " **bold** ". */
function wrapInlineMarker(content: string, marker: string): string {
  const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(content);
  const core = match?.[2] ?? "";
  if (!core) return content;
  return `${match?.[1] ?? ""}${marker}${core}${marker}${match?.[3] ?? ""}`;
}

/**
 * A code element whose pre wrapper fell outside the copied range is still
 * block code, recognizable by its highlighter line spans or embedded
 * newlines. Wrapping it like inline code produces backtick-surrounded
 * shell commands on paste.
 */
function isBlockCodeElement(element: Element, content: string): boolean {
  if (content.includes("\n")) return true;
  for (const child of element.childNodes) {
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).classList.contains("line")) {
      return true;
    }
  }
  return false;
}

function wrapInlineCode(code: string): string {
  const longestRun = [...(code.match(/`+/g) ?? [])].reduce(
    (max, run) => Math.max(max, run.length),
    0,
  );
  const fence = "`".repeat(Math.max(1, longestRun + (longestRun > 0 ? 1 : 0)));
  const pad = code.startsWith("`") || code.endsWith("`") ? " " : "";
  return `${fence}${pad}${code}${pad}${fence}`;
}

function serializeTableCell(cell: Element): string {
  return serializeChildren(cell).replace(/\n+/g, " ").trim().replaceAll("|", "\\|");
}

function tableSeparatorFor(headerCells: Element[]): string {
  const markers = headerCells.map((cell) => {
    // `||`, not `??`: `style.textAlign` is "" — not undefined — on an element
    // with no inline style, so `??` never falls through and the `align`
    // attribute branch is unreachable. react-markdown emits inline style, which
    // is why this holds in the source this was ported from.
    const align = (cell as HTMLElement).style?.textAlign || cell.getAttribute("align") || "";
    if (align === "center") return ":---:";
    if (align === "right") return "---:";
    return "---";
  });
  return `| ${markers.join(" | ")} |`;
}

function serializeTable(table: Element): string {
  const rows = [...table.querySelectorAll(":scope > thead > tr, :scope > tbody > tr, :scope > tr")];
  if (rows.length === 0) return "";
  const lines: string[] = [];
  let emittedSeparator = false;
  for (const row of rows) {
    const cells = [...row.children].filter(
      (cell) => cell.tagName === "TH" || cell.tagName === "TD",
    );
    if (cells.length === 0) continue;
    lines.push(`| ${cells.map((cell) => serializeTableCell(cell)).join(" | ")} |`);
    if (!emittedSeparator) {
      lines.push(tableSeparatorFor(cells));
      emittedSeparator = true;
    }
  }
  return `${lines.join("\n")}\n\n`;
}

function serializeAnchor(anchor: Element): string {
  const markdownCopy = anchor.getAttribute("data-markdown-copy");
  if (markdownCopy !== null) return markdownCopy;
  const content = serializeChildren(anchor);
  const href = anchor.getAttribute("href") ?? "";
  if (!/^https?:\/\//i.test(href)) return content;
  const label = content.trim();
  if (!label) return "";
  if (label === href) return href;
  return `[${label}](${href})`;
}

function serializeChildren(node: Node): string {
  let out = "";
  for (const child of node.childNodes) {
    out += serializeNode(child);
  }
  return out;
}

/**
 * Table cells hold inline content, so only the inline cases are handled here.
 *
 * t3code's version of this walker also serializes whole messages — headings,
 * lists, blockquotes, fenced code, nested tables — because it backs a
 * "copy message as markdown" action. Forge has not ported that action, so those
 * branches would be unreachable code carrying real complexity (the full walker
 * measures cyclomatic 39). Anything unhandled falls through to its children,
 * which is the right answer for a cell: the text survives, only the emphasis
 * markers are dropped.
 */
/** Tags whose only job is to wrap their children in a marker pair. */
const INLINE_EMPHASIS_MARKERS: Record<string, string> = {
  STRONG: "**",
  B: "**",
  EM: "*",
  I: "*",
  DEL: "~~",
  S: "~~",
};

/** Text nodes only. Element handling lives in `serializeElement`. */
function serializeTextNode(node: Node): string {
  const text = node.textContent ?? "";
  // Inter-block formatting whitespace from the renderer collapses to a
  // newline; real inline whitespace passes through untouched.
  return text.includes("\n") && text.trim().length === 0 ? "\n" : text;
}

/** Both halves are required: `![](src)` and `![alt]()` are equally useless. */
function serializeImage(element: Element): string {
  const alt = element.getAttribute("alt") ?? "";
  const src = element.getAttribute("src") ?? "";
  return alt && src ? `![${alt}](${src})` : "";
}

/** The handful of inline tags that need more than a marker pair. */
function serializeInlineElement(element: Element): string | null {
  switch (element.tagName) {
    case "BR":
      return "\n";
    case "CODE": {
      const content = element.textContent ?? "";
      return isBlockCodeElement(element, content) ? content : wrapInlineCode(content);
    }
    case "A":
      return serializeAnchor(element);
    case "IMG":
      return serializeImage(element);
    default:
      return null;
  }
}

function serializeElement(element: Element): string {
  const markdownCopy = element.getAttribute("data-markdown-copy");
  if (markdownCopy !== null) return markdownCopy;
  if (isSkippedElement(element)) return "";

  const emphasisMarker = INLINE_EMPHASIS_MARKERS[element.tagName];
  if (emphasisMarker) return wrapInlineMarker(serializeChildren(element), emphasisMarker);

  return serializeInlineElement(element) ?? serializeChildren(element);
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return serializeTextNode(node);
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  return serializeElement(node as Element);
}

/** Collapses serializer spacing artifacts without touching fenced code content. */
export function serializeTableElementToMarkdown(table: Element): string {
  return serializeTable(table).trim();
}

function csvCell(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return /[",\n]/.test(normalized) ? `"${normalized.replaceAll('"', '""')}"` : normalized;
}

export function serializeTableElementToCsv(table: Element): string {
  const rows = [...table.querySelectorAll(":scope > thead > tr, :scope > tbody > tr, :scope > tr")];
  const lines: string[] = [];
  for (const row of rows) {
    const cells = [...row.children].filter(
      (cell) => cell.tagName === "TH" || cell.tagName === "TD",
    );
    if (cells.length === 0) continue;
    lines.push(cells.map((cell) => csvCell(cell.textContent ?? "")).join(","));
  }
  return lines.join("\n");
}
